import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { formatMoney } from '@/utils/currency';

interface RideApp { label: string; scheme?: string; web: string }

// Type-aware placeholder tile when an ordered item has no photo (mirrors the menu list fallbacks).
const CC_FB: { re: RegExp; emoji: string; grad: string }[] = [
  { re: /whisk|scotch|bourbon|\brum\b|vodka|\bgin\b|brandy|tequila|cognac|liqueur|spirit/i, emoji: '🥃', grad: 'linear-gradient(135deg,#b45309,#f59e0b)' },
  { re: /beer|lager|\bale\b|stout|pilsner|\bipa\b|brew/i, emoji: '🍺', grad: 'linear-gradient(135deg,#d97706,#fcd34d)' },
  { re: /wine|champagne|prosecco|merlot|cabernet|shiraz/i, emoji: '🍷', grad: 'linear-gradient(135deg,#7f1d1d,#be123c)' },
  { re: /cocktail|mocktail|martini|mojito|margarita/i, emoji: '🍸', grad: 'linear-gradient(135deg,#0ea5e9,#67e8f9)' },
  { re: /coffee|espresso|latte|\btea\b|chai/i, emoji: '☕', grad: 'linear-gradient(135deg,#c49a6c,#e6cba5)' },
  { re: /juice|water|soda|cola|coke|pepsi|drink/i, emoji: '🥤', grad: 'linear-gradient(135deg,#38bdf8,#a5f3fc)' },
  { re: /pizza/i, emoji: '🍕', grad: 'linear-gradient(135deg,#ef4444,#fca5a5)' },
  { re: /burger|sandwich|fries|snack|kebab|tikka|wings|nuggets/i, emoji: '🍔', grad: 'linear-gradient(135deg,#f97316,#fdba74)' },
];
const ccFb = (name: string) => { for (const f of CC_FB) if (f.re.test(name || '')) return f; return { emoji: '🍽️', grad: 'linear-gradient(135deg,#FF8A5B,#FFD1B8)' }; };
/** Ordered-item thumbnail: real photo if present, else a type-based emoji tile. */
const CcThumb: React.FC<{ src?: string | null; name: string }> = ({ src, name }) => {
  const [err, setErr] = useState(false);
  if (src && !err) return <img src={src} alt={name} loading="lazy" onError={() => setErr(true)} style={S.orderImg} />;
  const fb = ccFb(name);
  return <div style={{ ...S.orderImg, background: fb.grad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }} aria-hidden>{fb.emoji}</div>;
};
// Fallback list if the server doesn't send a per-branch set.
const RIDE_APPS_FALLBACK: RideApp[] = [
  { label: '🚗 Uber', scheme: 'uber://?action=setPickup&pickup=my_location', web: 'https://m.uber.com/ul/?action=setPickup&pickup=my_location' },
  { label: '🗺️ Taxi stands', web: 'https://www.google.com/maps/search/?api=1&query=taxi+near+me' },
];

/**
 * Open a ride app in a NEW TAB so the Concierge chat is never navigated away. The web URLs are the
 * providers' universal links, so on mobile the OS opens the native app when it's installed and falls
 * back to the mobile web page otherwise.
 */
const openRide = (app: RideApp) => { window.open(app.web, '_blank', 'noopener'); };

interface Action { capability: string; kind: 'execute' | 'navigate' | 'present'; label: string; args: any }
interface Turn { role: 'user' | 'assistant'; text: string; action?: Action | null }
interface Props {
  bookingId: string;
  branchId: string;
  /** For 'navigate' actions (e.g. request_song) — parent opens the matching existing UI. */
  onNavigate?: (capability: string, args: any) => void;
  /** Launcher copy — customise the doorway (e.g. a menu-framed entry inside the order screen).
   *  Same Concierge/brain underneath; only the button label changes. */
  launchTitle?: string;
  launchSub?: string;
}

/**
 * "Your AI Concierge" — a launcher button that opens a full chat (full-screen on mobile, a dialog on
 * larger screens), so a long conversation never disturbs the home layout. The guest types or speaks and
 * the AI turns each message into an action (order / call staff / song / info). Renders only when AI is
 * available for the branch (same admin gate as the AI waiter / translation).
 */
const Concierge: React.FC<Props> = ({ bookingId, branchId, onNavigate, launchTitle, launchSub }) => {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const [songUrl, setSongUrl] = useState('');
  const [listening, setListening] = useState(false);
  const [persona, setPersona] = useState<{ name: string | null; tone: string | null; voiceEnabled: boolean; voice: string | null } | null>(null);
  const [muted, setMuted] = useState(false);
  const recRef = useRef<any>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const storeKey = `cc_chat_${bookingId}`;

  useEffect(() => {
    let alive = true;
    apiService.getTranslationAvailableForBranch(branchId).then((ok) => { if (alive) setAvailable(!!ok); }).catch(() => { if (alive) setAvailable(false); });
    apiService.getBranchAiAgent(branchId).then((p) => { if (alive) setPersona(p); }).catch(() => { if (alive) setPersona(null); });
    return () => { alive = false; };
  }, [branchId]);

  // Text-to-speech: when the branch's persona has voice on, speak the concierge's replies aloud.
  const speak = (t: string) => {
    try {
      if (!persona?.voiceEnabled || muted || !t) return;
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel();
      const u = new SpeechSynthesisUtterance(String(t).replace(/[*_#`>]/g, '').slice(0, 400));
      u.rate = 1; u.pitch = 1;
      // Prefer a voice matching the persona's requested voice id when available.
      const voices = synth.getVoices?.() || [];
      const want = (persona.voice || '').toLowerCase();
      const match = want && voices.find((v) => v.name.toLowerCase().includes(want));
      if (match) u.voice = match;
      synth.speak(u);
    } catch { /* TTS is best-effort */ }
  };
  // Stop speaking when the sheet closes.
  useEffect(() => { if (!open) { try { window.speechSynthesis?.cancel(); } catch { /* noop */ } } }, [open]);

  // Load this visit's chat history — server-side (like the staff chat) so it reloads from check-in on
  // any refresh/device. localStorage is just an instant cache while the server responds.
  useEffect(() => {
    if (!bookingId) return;
    try {
      const saved = localStorage.getItem(storeKey);
      if (saved) setTurns(JSON.parse(saved) || []);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('cc_chat_') && k !== storeKey) localStorage.removeItem(k);
      }
    } catch { /* ignore */ }
    apiService.conciergeHistory(bookingId)
      // Only overwrite from the server when it actually has messages — an empty thread must not wipe a
      // just-shown greeting (the greeting effect below fills a fresh thread).
      .then((r) => { const h = (r as any)?.data; if (Array.isArray(h) && h.length) setTurns(h.map((m: any) => ({ role: m.role, text: m.text, action: null }))); })
      .catch(() => { /* fall back to the localStorage cache */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // Proactive personalised opener: the first time a recognised regular opens the concierge on a fresh
  // thread, greet them by name and lead with their usual/last items as one-tap cards.
  const [postOrder, setPostOrder] = useState<any | null>(null); // "anything else?" attach suggestion after an order
  const [feedback, setFeedback] = useState<{ itemName: string; stockItemId: string | null } | null>(null);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const greetedRef = useRef(false);
  useEffect(() => {
    if (!open || greetedRef.current || !bookingId) return;
    greetedRef.current = true;
    apiService.conciergeGreeting(bookingId).then((r) => {
      const g = (r as any)?.data;
      if (!g?.reply) return;
      // Don't interrupt an existing conversation — only greet a still-empty thread.
      setTurns((t) => (t.length === 0 ? [{ role: 'assistant', text: g.reply, action: null }] : t));
      if (g.action) setAction((a) => a || g.action);
      if (g.feedbackItem?.itemName) setFeedback(g.feedbackItem);
      apiService.conciergeAppendMessage(bookingId, 'assistant', g.reply).catch(() => {});
    }).catch(() => { /* greeting is best-effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  // Persist the thread as it changes (capped, so storage stays small).
  useEffect(() => {
    if (!bookingId) return;
    try { localStorage.setItem(storeKey, JSON.stringify(turns.slice(-40))); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns, bookingId]);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ block: 'end' }); }, [turns, action, open]);

  // Lock the page behind the modal so scrolling the chat never moves the background (iOS + Android).
  useEffect(() => {
    if (!open) return;
    const y = window.scrollY;
    const b = document.body;
    const prev = { position: b.style.position, top: b.style.top, width: b.style.width, overflow: b.style.overflow };
    b.style.position = 'fixed'; b.style.top = `-${y}px`; b.style.width = '100%'; b.style.overflow = 'hidden';
    return () => {
      b.style.position = prev.position; b.style.top = prev.top; b.style.width = prev.width; b.style.overflow = prev.overflow;
      window.scrollTo(0, y);
    };
  }, [open]);

  const say = (role: Turn['role'], text: string, act?: Action | null) => {
    setTurns((t) => [...t, { role, text, action: act || null }]);
    // Persist to the server so the thread survives refresh (like the staff chat). Fire-and-forget.
    if (text) apiService.conciergeAppendMessage(bookingId, role, text).catch(() => {});
    // Speak live assistant replies aloud when the persona's voice is on (history load bypasses say()).
    if (role === 'assistant') speak(text);
  };

  const ask = async (q?: string) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(''); setAction(null); setSongUrl('');
    // Send the recent conversation so the AI has short-term memory ("make it two", "same again", …).
    const history = turns.slice(-8).map((t) => ({ role: t.role, text: t.text }));
    say('user', message);
    setBusy(true);
    try {
      const r = await apiService.conciergeAsk(bookingId, message, history);
      const d: any = r?.data;
      say('assistant', d?.reply || '…', d?.action || null);
      if (d?.action) setAction(d.action as Action);
    } catch (e: any) {
      if (e?.message === 'AI is not available') { setAvailable(false); setOpen(false); return; }
      say('assistant', 'Sorry, I couldn’t get that — try the buttons below.');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!action) return;
    if (action.kind === 'navigate') { onNavigate?.(action.capability, action.args); setAction(null); setOpen(false); return; }
    const suggestion = action.capability === 'order' ? action.args?.suggestion : null;
    const suggCurrency = action.args?.currency;
    setBusy(true);
    try {
      const r = await apiService.conciergeExecute(bookingId, action.capability, action.args);
      say('assistant', r?.data?.message || 'Done ✓');
      setAction(null);
      // "Would you like fries with that?" — offer one complementary add after the order lands.
      if (suggestion?.itemName) setPostOrder({ ...suggestion, currency: suggCurrency });
      try { (navigator as any).vibrate?.(60); } catch { /* noop */ }
    } catch (e: any) {
      say('assistant', e?.message || 'Could not complete that.');
    } finally { setBusy(false); }
  };

  // Add a single item straight from a presented menu card (one-tap ordering while browsing).
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [added, setAdded] = useState<Record<string, boolean>>({});
  const addFromMenu = async (it: any, key: string, currency?: string) => {
    if (addingKey || added[key]) return;
    // Sized item (e.g. a whisky pour) → order the smallest pour so a price is always attached.
    const size = Array.isArray(it.sizes) && it.sizes.length ? it.sizes[0] : null;
    const orderItem = {
      stockItemId: it.stockItemId || null,
      itemName: size ? `${it.itemName} · ${size.label}` : it.itemName,
      quantity: 1,
      unitPrice: size ? Number(size.price) : (it.unitPrice != null ? Number(it.unitPrice) : null),
      itemImage: it.itemImage || null,
    };
    setAddingKey(key);
    try {
      const r = await apiService.conciergeExecute(bookingId, 'order', { items: [orderItem], currency });
      setAdded((m) => ({ ...m, [key]: true }));
      say('assistant', r?.data?.message || `Added ${orderItem.itemName} ✓`);
      try { (navigator as any).vibrate?.(50); } catch { /* noop */ }
    } catch (e: any) {
      say('assistant', e?.message || 'Could not add that.');
    } finally { setAddingKey(null); }
  };

  // "Hope you enjoyed it?" — one-tap 👍/👎 on their last item.
  const sendFeedback = async (sentiment: 'up' | 'down') => {
    if (!feedback || feedbackBusy) return;
    setFeedbackBusy(true);
    const fb = feedback;
    setFeedback(null); // optimistic dismiss
    try {
      const r = await apiService.conciergeFeedback(bookingId, fb.itemName, fb.stockItemId, sentiment);
      say('assistant', r?.data?.message || (sentiment === 'up' ? 'Glad you enjoyed it! 😊' : 'Thanks — I\'ll pass that on.'));
      try { (navigator as any).vibrate?.(50); } catch { /* noop */ }
    } catch { /* best-effort; already dismissed */ }
    finally { setFeedbackBusy(false); }
  };

  // Song request — reuse the existing "paste a YouTube link" flow inline (no player/search).
  const submitSong = async () => {
    const url = songUrl.trim();
    if (!url || busy) return;
    setBusy(true);
    try {
      await apiService.createSongRequest(bookingId, url);
      say('assistant', 'Added to the queue 🎵');
      setAction(null); setSongUrl('');
      try { (navigator as any).vibrate?.(60); } catch { /* noop */ }
    } catch (e: any) {
      say('assistant', e?.message || 'Please paste a valid YouTube link.');
    } finally { setBusy(false); }
  };

  const clearChat = () => {
    setTurns([]); setAction(null); setText(''); setSongUrl('');
    try { localStorage.removeItem(storeKey); } catch { /* ignore */ }
    apiService.conciergeClearHistory(bookingId).catch(() => {}); // also clear the server copy
  };

  // Voice input (Web Speech API where available) — great in a loud bar / hands-full moment.
  const canVoice = typeof window !== 'undefined' && ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  const toggleVoice = () => {
    if (!canVoice) return;
    if (listening) { try { recRef.current?.stop(); } catch { /* noop */ } setListening(false); return; }
    try {
      const Rec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const rec = new Rec();
      rec.lang = 'en-IN'; rec.interimResults = false; rec.maxAlternatives = 1;
      rec.onresult = (ev: any) => { const said = ev?.results?.[0]?.[0]?.transcript || ''; setListening(false); if (said) ask(said); };
      rec.onerror = () => setListening(false);
      rec.onend = () => setListening(false);
      recRef.current = rec; setListening(true); rec.start();
    } catch { setListening(false); }
  };

  if (available !== true) return null;

  return (
    <>
      {/* Launcher — a compact, titled button on the home screen (doesn't grow with the conversation). */}
      <button style={S.launcher} onClick={() => setOpen(true)} type="button">
        <span style={S.launchIcon}>✨</span>
        <span style={S.launchText}>
          <span style={S.launchTitle}>{launchTitle || (persona?.name ? `Ask ${persona.name}` : 'Your AI Concierge')}</span>
          <span style={S.launchSub}>{launchSub || (persona?.name ? `Your ${persona.tone || 'friendly'} concierge — order, ask, call staff` : 'Ask, order, call staff — just say it')}</span>
        </span>
        <span style={S.launchArrow}>›</span>
      </button>

      {open && (
        <div style={S.overlay} onClick={() => setOpen(false)}>
          {/* Full-screen on mobile, a centered windowed dialog on wider screens. */}
          <style>{`.cc-sheet{width:100%;max-width:100%;height:100dvh;border-radius:0}@media(min-width:640px){.cc-sheet{max-width:560px;height:min(100dvh,760px);border-radius:16px}}`}</style>
          <div className="cc-sheet" style={S.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={S.head}>
              <span style={S.spark}>✨</span>
              <span style={S.title}>{persona?.name || 'Your AI Concierge'}</span>
              {persona?.voiceEnabled && (
                <button style={S.muteBtn} onClick={() => { setMuted((m) => !m); try { window.speechSynthesis?.cancel(); } catch { /* noop */ } }} aria-label={muted ? 'Unmute voice' : 'Mute voice'} type="button">{muted ? '🔇' : '🔊'}</button>
              )}
              {turns.length > 0 && <button style={S.clearChat} onClick={clearChat} type="button">Clear</button>}
              <button style={S.closeBtn} onClick={() => setOpen(false)} aria-label="Close" type="button">✕</button>
            </div>

            <div style={S.thread}>
              {turns.length === 0 && (
                <div style={S.hint}>
                  <div style={S.hintTitle}>Hi 👋 What do you need?</div>
                  <div style={S.hintList}>Try: “one more beer” · “clean the table” · “what’s on tonight?” · “nearest ATM”</div>
                </div>
              )}
              {turns.map((t, i) => (
                <div key={i} style={{ ...S.bubbleRow, justifyContent: t.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ ...S.bubble, ...(t.role === 'user' ? S.bubbleUser : S.bubbleBot) }}>{t.text}</div>
                </div>
              ))}
              {busy && <div style={{ ...S.bubbleRow, justifyContent: 'flex-start' }}><div style={{ ...S.bubble, ...S.bubbleBot }}>…</div></div>}

              {postOrder && (
                <div style={S.postOrder}>
                  <CcThumb src={postOrder.itemImage} name={postOrder.itemName} />
                  <div style={S.postInfo}>
                    <span style={S.postKicker}>Anything else?</span>
                    <span style={S.postName}>{postOrder.itemName}{(postOrder.sizes?.length || postOrder.unitPrice != null) && (
                      <span style={S.postPrice}> · {postOrder.sizes?.length ? `From ${formatMoney(Number(postOrder.fromPrice), postOrder.currency)}` : formatMoney(Number(postOrder.unitPrice), postOrder.currency)}</span>
                    )}</span>
                  </div>
                  <button style={S.postAdd} disabled={!!addingKey} onClick={() => { void addFromMenu(postOrder, 'postorder', postOrder.currency); setPostOrder(null); }} type="button">＋ Add</button>
                  <button style={S.postNo} onClick={() => setPostOrder(null)} type="button" aria-label="No thanks">✕</button>
                </div>
              )}

              {feedback && (
                <div style={S.fbBar}>
                  <span style={S.fbText}>How was the {feedback.itemName} last time?</span>
                  <div style={S.fbBtns}>
                    <button style={S.fbBtn} disabled={feedbackBusy} onClick={() => sendFeedback('up')} type="button" aria-label="Loved it">👍</button>
                    <button style={S.fbBtn} disabled={feedbackBusy} onClick={() => sendFeedback('down')} type="button" aria-label="Not great">👎</button>
                  </div>
                </div>
              )}

              {action && action.capability === 'get_ride' ? (
                <div style={S.rideWrap}>
                  {(((action.args?.apps as RideApp[]) || RIDE_APPS_FALLBACK)).map((r) => (
                    <button key={r.label} style={S.rideBtn} onClick={() => openRide(r)} type="button">{r.label}</button>
                  ))}
                </div>
              ) : action && action.capability === 'request_song' ? (
                <div style={S.songBox}>
                  <input style={S.songInput} value={songUrl} onChange={(e) => setSongUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submitSong(); }} placeholder="Paste a YouTube link…" autoFocus />
                  <button style={S.send} onClick={submitSong} disabled={busy || !songUrl.trim()} type="button">{busy ? '…' : '🎵'}</button>
                </div>
              ) : action && action.capability === 'order' && Array.isArray(action.args?.items) && action.args.items.length ? (
                <div style={S.orderCard}>
                  <div style={S.orderItems}>
                    {action.args.items.map((it: any, i: number) => (
                      <div key={i} style={S.orderRow}>
                        <CcThumb src={it.itemImage} name={it.itemName} />
                        <span style={S.orderName}>{it.quantity}× {it.itemName}</span>
                        {it.unitPrice != null && <span style={S.orderPrice}>{formatMoney(Number(it.unitPrice) * (it.quantity || 1), action.args?.currency)}</span>}
                      </div>
                    ))}
                  </div>
                  <div style={S.orderActions}>
                    <button style={S.cancel} onClick={() => setAction(null)} type="button">Cancel</button>
                    <button style={S.confirm} onClick={confirm} disabled={busy} type="button">{busy ? '…' : 'Confirm order'}</button>
                  </div>
                </div>
              ) : action && action.capability === 'show_menu' && Array.isArray(action.args?.items) && action.args.items.length ? (
                <div style={S.menuList}>
                  {action.args.items.map((it: any, i: number) => {
                    const key = `${it.stockItemId || it.itemName}:${i}`;
                    const price = it.sizes?.length ? `From ${formatMoney(Number(it.fromPrice), action.args?.currency)}`
                      : (it.unitPrice != null ? formatMoney(Number(it.unitPrice), action.args?.currency) : '');
                    return (
                      <div key={key} style={S.menuCard}>
                        <CcThumb src={it.itemImage} name={it.itemName} />
                        <div style={S.menuInfo}>
                          <span style={S.menuName}>{it.itemName}</span>
                          {it.featured && <span style={S.menuStar}>★ {it.featuredLabel || 'Featured'}</span>}
                          {!it.featured && it.category && <span style={S.menuCat}>{it.category}</span>}
                          {price && <span style={S.menuPrice}>{price}</span>}
                        </div>
                        <button
                          style={{ ...S.addBtn, ...(added[key] ? S.addedBtn : {}) }}
                          onClick={() => addFromMenu(it, key, action.args?.currency)}
                          disabled={!!addingKey || !!added[key]}
                          type="button"
                        >
                          {added[key] ? '✓ Added' : addingKey === key ? '…' : '＋ Add'}
                        </button>
                      </div>
                    );
                  })}
                  <button style={S.menuDone} onClick={() => setAction(null)} type="button">Done</button>
                </div>
              ) : action ? (
                <div style={S.actionCard}>
                  <span style={S.actionLabel}>{action.label}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={S.cancel} onClick={() => setAction(null)} type="button">Cancel</button>
                    <button style={S.confirm} onClick={confirm} disabled={busy} type="button">{busy ? '…' : action.kind === 'navigate' ? 'Open' : 'Confirm'}</button>
                  </div>
                </div>
              ) : null}
              <div ref={endRef} />
            </div>

            <div style={S.inputRow}>
              <div style={S.inputWrap}>
                <input
                  style={S.inputInner}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
                  placeholder={listening ? 'Listening…' : 'Message your concierge…'}
                  maxLength={200}
                  autoFocus
                />
                {text && <button style={S.clearX} onClick={() => setText('')} aria-label="Clear" type="button">×</button>}
              </div>
              {canVoice && <button style={{ ...S.mic, ...(listening ? S.micOn : {}) }} onClick={toggleVoice} aria-label="Speak" type="button">🎤</button>}
              <button style={S.send} onClick={() => ask()} disabled={busy || !text.trim()} type="button">{busy ? '…' : '→'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const S: Record<string, React.CSSProperties> = {
  // Launcher button
  launcher: { display: 'flex', alignItems: 'center', gap: 13, width: '100%', background: 'linear-gradient(135deg,#fff4ed,#ffe9dc)', border: '1px solid rgba(255,107,53,0.3)', borderRadius: 16, padding: '13px 15px', cursor: 'pointer', textAlign: 'left', marginBottom: 12, boxShadow: '0 2px 10px rgba(255,107,53,0.10)' },
  launchIcon: { width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 },
  launchText: { flex: 1, display: 'flex', flexDirection: 'column', gap: 1 },
  launchTitle: { fontSize: '1rem', fontWeight: 800, color: '#0f172a' },
  launchSub: { fontSize: '0.8rem', color: '#b4643f', fontWeight: 600 },
  launchArrow: { color: '#e0531f', fontSize: 22, fontWeight: 800 },

  // Modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 },
  sheet: { background: '#fbfbfc', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px', background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff' },
  spark: { fontSize: 20 },
  title: { flex: 1, fontSize: '1.05rem', fontWeight: 800, color: '#fff' },
  clearChat: { background: 'rgba(255,255,255,0.22)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', padding: '5px 11px', borderRadius: 999 },
  muteBtn: { background: 'rgba(255,255,255,0.22)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 10, fontSize: 15, cursor: 'pointer' },
  closeBtn: { background: 'rgba(255,255,255,0.22)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 10, fontSize: 15, fontWeight: 800, cursor: 'pointer' },

  thread: { flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 } as React.CSSProperties,
  hint: { textAlign: 'center', color: '#64748b', padding: '24px 12px' },
  hintTitle: { fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', marginBottom: 8 },
  hintList: { fontSize: '0.86rem', lineHeight: 1.6 },
  bubbleRow: { display: 'flex' },
  bubble: { maxWidth: '82%', padding: '10px 13px', borderRadius: 15, fontSize: '0.93rem', lineHeight: 1.4, whiteSpace: 'pre-line', wordBreak: 'break-word' },
  bubbleUser: { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', borderBottomRightRadius: 5 },
  bubbleBot: { background: '#fff', color: '#0f172a', border: '1px solid #eef1f5', borderBottomLeftRadius: 5 },

  rideWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 2 },
  rideBtn: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999, padding: '9px 15px', fontWeight: 700, fontSize: '0.88rem', color: '#0f172a', cursor: 'pointer' },
  songBox: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 },
  songInput: { flex: 1, background: '#fff', border: '1px solid #ffd8c2', borderRadius: 12, padding: '11px 13px', fontSize: '0.92rem', outline: 'none', color: '#0f172a' },
  actionCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', background: '#fff', border: '1px solid rgba(255,107,53,0.3)', borderRadius: 12, padding: '10px 12px', marginTop: 2 },
  orderCard: { background: '#fff', border: '1px solid rgba(255,107,53,0.3)', borderRadius: 14, padding: 12, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 2px 10px rgba(15,23,42,0.05)' },
  orderItems: { display: 'flex', flexDirection: 'column', gap: 10 },
  orderRow: { display: 'flex', alignItems: 'center', gap: 11 },
  orderImg: { width: 50, height: 50, borderRadius: 11, objectFit: 'cover', flexShrink: 0, display: 'block' },
  orderName: { flex: 1, minWidth: 0, fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' },
  orderPrice: { fontWeight: 800, fontSize: '0.9rem', color: '#e0531f', whiteSpace: 'nowrap' },
  orderActions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  actionLabel: { fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', flex: 1, minWidth: 130 },
  cancel: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 10, padding: '9px 14px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' },
  confirm: { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer' },

  // Presented menu (show_menu) — line-by-line picture cards with one-tap Add.
  menuList: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 },
  menuCard: { display: 'flex', alignItems: 'center', gap: 11, background: '#fff', border: '1px solid #eef1f5', borderRadius: 14, padding: 8, boxShadow: '0 2px 10px rgba(15,23,42,0.04)' },
  menuInfo: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  menuName: { fontWeight: 800, fontSize: '0.95rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  menuCat: { fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3 },
  menuStar: { alignSelf: 'flex-start', fontSize: '0.68rem', fontWeight: 800, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 9999, padding: '1px 8px', textTransform: 'uppercase', letterSpacing: 0.3 },
  postOrder: { display: 'flex', alignItems: 'center', gap: 10, background: 'linear-gradient(135deg,#fff7ed,#fff)', border: '1px solid rgba(255,107,53,0.28)', borderRadius: 14, padding: 9, marginTop: 2, boxShadow: '0 2px 10px rgba(224,83,31,0.06)' },
  postInfo: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 },
  postKicker: { fontSize: '0.68rem', fontWeight: 800, color: '#e0531f', textTransform: 'uppercase', letterSpacing: 0.4 },
  postName: { fontSize: '0.92rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  postPrice: { fontWeight: 700, color: '#e0531f' },
  postAdd: { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 12px', fontWeight: 800, fontSize: '0.84rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  postNo: { background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '0.95rem', cursor: 'pointer', flexShrink: 0, padding: '4px 6px' },
  fbBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#fff', border: '1px solid #eef1f5', borderRadius: 12, padding: '8px 12px', marginTop: 2, boxShadow: '0 2px 10px rgba(15,23,42,0.04)' },
  fbText: { fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', minWidth: 0 },
  fbBtns: { display: 'flex', gap: 6, flexShrink: 0 },
  fbBtn: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '6px 12px', fontSize: '1.05rem', lineHeight: 1, cursor: 'pointer' },
  menuPrice: { fontWeight: 800, fontSize: '0.86rem', color: '#e0531f' },
  addBtn: { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 13px', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  addedBtn: { background: '#16a34a', cursor: 'default' },
  menuDone: { alignSelf: 'center', background: 'transparent', color: '#64748b', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', padding: '6px 12px', marginTop: 2 },

  inputRow: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderTop: '1px solid #eef1f5', background: '#fff' },
  inputWrap: { flex: 1, position: 'relative', display: 'flex', alignItems: 'center' },
  inputInner: { flex: 1, background: '#f1f5f9', border: 'none', borderRadius: 12, padding: '12px 34px 12px 14px', fontSize: '0.95rem', outline: 'none', color: '#0f172a', width: '100%', boxSizing: 'border-box' },
  clearX: { position: 'absolute', right: 8, background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '50%', width: 22, height: 22, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  mic: { width: 44, height: 44, borderRadius: 12, border: '1px solid #ffd8c2', background: '#fff', fontSize: 18, cursor: 'pointer', flexShrink: 0 },
  micOn: { background: '#FF6B35', borderColor: '#FF6B35' },
  send: { width: 44, height: 44, borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', fontSize: 20, fontWeight: 800, cursor: 'pointer', flexShrink: 0 },
};

export default Concierge;
